import {useMemo} from 'react'
import {Button, Form, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {ICampaignSummary} from '../../../types/campaigns'
import clsx from 'clsx'

type Props = {
  campaigns: ICampaignSummary[]
  selectedId: string | null
  searchTerm: string
  onSearchChange: (value: string) => void
  onSelect: (id: string) => void
  onCreateCampaign: () => void
  loading?: boolean
}

const statusVariant: Record<string, string> = {
  DRAFT: 'badge-light-secondary',
  ACTIVE: 'badge-light-success',
  PAUSED: 'badge-light-warning',
  FINISHED: 'badge-light-info',
}

const formatDate = (value?: string | null) => {
  if (!value) return 'No schedule'
  try {
    return new Date(value).toLocaleString()
  } catch (error) {
    return value
  }
}

export const CampaignSidebar = ({
  campaigns,
  selectedId,
  searchTerm,
  onSearchChange,
  onSelect,
  onCreateCampaign,
  loading,
}: Props) => {
  const filtered = useMemo(() => {
    if (!searchTerm) return campaigns
    const term = searchTerm.toLowerCase()
    return campaigns.filter((campaign) => {
      const fields = [campaign.name, campaign.description ?? '', campaign.status, campaign.scheduleType]
      return fields.some((field) => field?.toLowerCase().includes(term))
    })
  }, [campaigns, searchTerm])

  return (
    <div className='campaigns-sidebar card'>
      <div className='card-header'>
        <div>
          <h5 className='mb-1'>Campaigns</h5>
          <span className='text-muted fw-semibold'>Centralized view of every outreach strategy</span>
        </div>
        <Button size='sm' variant='primary' onClick={onCreateCampaign}>
          New campaign
        </Button>
      </div>
      <div className='card-body border-top'>
        <Form.Control
          type='search'
          placeholder='Search by name, status or schedule'
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>
      <div className='campaigns-sidebar__list'>
        {loading && <div className='campaigns-sidebar__empty text-muted'>Loading campaigns...</div>}
        {!loading && filtered.length === 0 && (
          <div className='campaigns-sidebar__empty text-muted'>
            No campaigns match <strong>{searchTerm || 'your filters'}</strong>
          </div>
        )}
        {filtered.map((campaign) => (
          <button
            type='button'
            key={campaign.id}
            className={clsx('campaigns-sidebar__item', selectedId === campaign.id && 'campaigns-sidebar__item--active')}
            onClick={() => onSelect(campaign.id)}
          >
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <div className='fw-bold text-start'>{campaign.name}</div>
              <span className={clsx('badge', statusVariant[campaign.status] ?? 'badge-light')}>{campaign.status}</span>
            </div>
            <div className='text-muted campaigns-sidebar__item-description'>{campaign.description || 'No description provided'}</div>
            <div className='d-flex justify-content-between align-items-center mt-3'>
              <span className='text-gray-600 fw-semibold text-uppercase fs-8'>{campaign.scheduleType}</span>
              <OverlayTrigger overlay={<Tooltip id={`campaign-${campaign.id}-schedule`}>{formatDate(campaign.scheduledTime)}</Tooltip>} placement='top'>
                <span className='text-gray-500 text-end'>{campaign.scheduledTime ? formatDate(campaign.scheduledTime) : 'Not scheduled'}</span>
              </OverlayTrigger>
            </div>
            <div className='mt-2 d-flex flex-wrap gap-2 fs-8 text-gray-600'>
              <span>Targets: {campaign.totalTargets}</span>
              <span>Sent: {campaign.sentCount}</span>
              <span>Delivered: {campaign.deliveredCount}</span>
              <span>Failed: {campaign.failedCount}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
